import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabase";
import { SQL_SCENARIOS_PROBLEMS } from "./sqlScenariosProblems";
import { matchesProblem, searchSqlProblems } from "./sqlSearch";
import Editor from "@monaco-editor/react";
import ShareModal from "../ShareModel";
import { useProStatus } from "../hooks/useProStatus";
import { useMobile } from "../hooks/useMobile";
import MobileSQLLayout from "../components/MobileSQLLayout";
import { checkAndSaveBadges } from "../badges/useBadges";
import BadgeUnlockModal from "../badges/BadgeUnlockModal";
import { usePageMeta } from "../hooks/usePageMeta"; 
import StructuredData from "../components/StructuredData";

import useTimer from "../hooks/useTimer";
import TimerDisplay from "../components/TimerDisplay";
import TimesUpModal from "../components/TimesUpModal";
import PerformanceRating from "../components/PerformanceRating";


const MILESTONES = [
  {
    id: "bronze",
    label: "Scenario Ready",
    icon: "🥉",
    tier: "bronze",
    color: "#CD7F32",
    bg: "#fdf3e7",
    border: "#e8c49a",
    range: [1, 25],
    badge: "scenarios_bronze",
  },
  {
    id: "silver",
    label: "Hiring Manager's Pick",
    icon: "🥈",
    tier: "silver",
    color: "#9BA8B0",
    bg: "#f4f6f7",
    border: "#c8d0d4",
    range: [26, 50],
    badge: "scenarios_silver",
  },
  {
    id: "gold",
    label: "Scenario Cracker",
    icon: "🥇",
    tier: "gold",
    color: "#D4A017",
    bg: "#fffbeb",
    border: "#fde68a",
    range: [51, 100],
    badge: "scenarios_gold",
  },
];


const DB_SCHEMAS = {
  customers: [
    { name: "customer_id", type: "int64" },
    { name: "customer_name", type: "string" },
    { name: "email", type: "string" },
    { name: "phone", type: "string" },
    { name: "city", type: "string" },
    { name: "state", type: "string" },
    { name: "country", type: "string" },
    { name: "postal_code", type: "string" },
    { name: "created_date", type: "datetime" },
    { name: "activated_date", type: "datetime" },
    { name: "last_login_date", type: "datetime" },
    { name: "last_order_date", type: "datetime" },
    { name: "status", type: "string" },
    { name: "customer_type", type: "string" },
    { name: "acquisition_channel", type: "string" },
    { name: "lifetime_value", type: "decimal" },
    { name: "is_verified", type: "boolean" },
    { name: "created_at", type: "datetime" },
    { name: "updated_at", type: "datetime" }
  ],
  delivery_partners: [
    { name: "delivery_partner_id", type: "int64" },
    { name: "partner_name", type: "string" },
    { name: "phone", type: "string" },
    { name: "vehicle_type", type: "string" },
    { name: "vehicle_number", type: "string" },
    { name: "city", type: "string" },
    { name: "status", type: "string" },
    { name: "joining_date", type: "datetime" },
    { name: "last_active_date", type: "datetime" },
    { name: "rating", type: "decimal" },
    { name: "total_deliveries", type: "int64" },
    { name: "created_at", type: "datetime" },
    { name: "updated_at", type: "datetime" }
  ],
  feedback: [
    { name: "feedback_id", type: "int64" },
    { name: "customer_id", type: "int64" },
    { name: "order_id", type: "int64" },
    { name: "rating", type: "decimal" },
    { name: "review_text", type: "string" },
    { name: "feedback_channel", type: "string" },
    { name: "issue_category", type: "string" },
    { name: "created_at", type: "datetime" },
    { name: "updated_at", type: "datetime" }
  ],
  order_items: [
    { name: "item_id", type: "int64" },
    { name: "order_id", type: "int64" },
    { name: "product_id", type: "int64" },
    { name: "quantity", type: "int64" },
    { name: "unit_price", type: "decimal" },
    { name: "discount_amount", type: "decimal" },
    { name: "tax_amount", type: "decimal" },
    { name: "total_price", type: "decimal" },
    { name: "item_status", type: "string" },
    { name: "currency", type: "string" },
    { name: "created_at", type: "datetime" },
    { name: "updated_at", type: "datetime" }
  ],
  orders: [
    { name: "order_id", type: "int64" },
    { name: "customer_id", type: "int64" },
    { name: "order_date", type: "datetime" },
    { name: "order_status", type: "string" },
    { name: "payment_status", type: "string" },
    { name: "delivery_partner_id", type: "int64" },
    { name: "subtotal_amount", type: "decimal" },
    { name: "tax_amount", type: "decimal" },
    { name: "discount_amount", type: "decimal" },
    { name: "delivery_fee", type: "decimal" },
    { name: "total_amount", type: "decimal" },
    { name: "currency", type: "string" },
    { name: "estimated_delivery_time", type: "datetime" },
    { name: "out_for_delivery_time", type: "datetime" },
    { name: "delivered_date", type: "datetime" },
    { name: "cancelled_date", type: "datetime" },
    { name: "cancellation_reason", type: "string" },
    { name: "created_at", type: "datetime" },
    { name: "updated_at", type: "datetime" }
  ],
  payments: [
    { name: "payment_id", type: "int64" },
    { name: "order_id", type: "int64" },
    { name: "payment_method", type: "string" },
    { name: "payment_provider", type: "string" },
    { name: "transaction_reference", type: "string" },
    { name: "payment_status", type: "string" },
    { name: "amount", type: "decimal" },
    { name: "currency", type: "string" },
    { name: "refund_amount", type: "decimal" },
    { name: "refund_date", type: "datetime" },
    { name: "failure_reason", type: "string" },
    { name: "payment_date", type: "datetime" },
    { name: "attempt_number", type: "int64" },
    { name: "created_at", type: "datetime" },
    { name: "updated_at", type: "datetime" }
  ],
  products: [
    { name: "product_id", type: "int64" },
    { name: "product_name", type: "string" },
    { name: "product_description", type: "string" },
    { name: "category", type: "string" },
    { name: "subcategory", type: "string" },
    { name: "brand", type: "string" },
    { name: "sku", type: "string" },
    { name: "price", type: "decimal" },
    { name: "cost_price", type: "decimal" },
    { name: "currency", type: "string" },
    { name: "is_active", type: "boolean" },
    { name: "created_at", type: "datetime" },
    { name: "updated_at", type: "datetime" }
  ]
};

const getTypeBadgeStyle = (type) => {
  switch (type) {
    case "int64": return { color: "#0ea5e9", bg: "#f0f9ff" };     // Sky blue
    case "boolean": return { color: "#16a34a", bg: "#f0fdf4" };   // Emerald green
    case "string": return { color: "#a855f7", bg: "#f3e8ff" };    // Purple text badge
    case "decimal": return { color: "#06b6d4", bg: "#ecfeff" };   // Teal decimal badge
    case "datetime": return { color: "#ea580c", bg: "#fff7ed" };  // Orange timestamp badge
    default: return { color: "#64748b", bg: "#f1f5f9" };         // Fallback slate
  }
};

function TableStructure({ tables }) {
  const [openTable, setOpenTable] = useState(null);

  return (
    <div style={{ marginTop: "0.75rem" }}>
      <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>
        📋 Table Structure
      </div>
      {tables.map(table => (
        <div key={table} style={{ marginBottom: "4px", border: "1px solid #e2e8f0", borderRadius: "6px", overflow: "hidden" }}>
          <div
            onClick={() => setOpenTable(openTable === table ? null : table)}
            style={{ padding: "6px 10px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", background: openTable === table ? "#eff6ff" : "#f8fafc" }}
          >
            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: openTable === table ? "#2563eb" : "#0f172a", fontFamily: "monospace" }}>
              {table}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "0.65rem", color: "#94a3b8" }}>{DB_SCHEMAS[table]?.length || 0} cols</span>
              <span style={{ fontSize: "0.65rem", color: "#94a3b8", transform: openTable === table ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▾</span>
            </div>
          </div>
          {openTable === table && (
            <div style={{ background: "#ffffff", borderTop: "1px solid #e2e8f0" }}>
              {(DB_SCHEMAS[table] || []).map((col, i) => {
                const style = getTypeBadgeStyle(col.type);
                return (
                  <div
                    key={col.name}
                    style={{ padding: "5px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: i < DB_SCHEMAS[table].length - 1 ? "1px solid #f1f5f9" : "none" }}
                  >
                    <span style={{ fontSize: "0.72rem", fontFamily: "monospace", color: "#0f172a" }}>{col.name}</span>
                    <span style={{ fontSize: "0.62rem", fontWeight: 600, padding: "1px 6px", borderRadius: "4px", background: style.bg, color: style.color }}>
                      {col.type}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ProblemRow({ p, isSelected, isExpanded, isSolved, isLocked, selectedItemRef, diffStyle, onSelect, nested = false }) {
  return (
    <div
      ref={isSelected ? selectedItemRef : null}
      style={{
        background: "#ffffff",
        border: nested ? "none" : "1.5px solid",
        borderColor: isSelected ? "#2563eb" : "#e2e8f0",
        borderBottom: nested ? "1px solid #f1f5f9" : undefined,
        borderRadius: nested ? "0" : "10px",
        overflow: "hidden",
        transition: "border-color 0.15s",
        boxShadow: isSelected && !nested ? "0 0 0 3px rgba(37,99,235,0.08)" : "none",
      }}
    >
      <div
        onClick={onSelect}
        style={{ padding: "0.75rem 0.875rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", background: isSelected ? "#f8faff" : "transparent" }}
      >
        <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: isSolved ? "#16a34a" : isSelected ? "#eff6ff" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.62rem", fontWeight: 700, color: isSolved ? "#fff" : isSelected ? "#2563eb" : "#94a3b8", flexShrink: 0 }}>
          {isSolved ? "✓" : p.id}
        </div>
        <span style={{ fontSize: "0.83rem", fontWeight: isSelected ? 700 : 500, color: isSelected ? "#0f172a" : "#334155", flex: 1, lineHeight: 1.35 }}>
          {p.title}
        </span>
        <span style={{ fontSize: "0.62rem", padding: "2px 7px", borderRadius: "10px", background: (diffStyle[p.difficulty] || diffStyle.Easy).bg, color: (diffStyle[p.difficulty] || diffStyle.Easy).color, border: `1px solid ${(diffStyle[p.difficulty] || diffStyle.Easy).border}`, fontWeight: 600, whiteSpace: "nowrap" }}>
  {p.difficulty || "Easy"}
</span>
        <span style={{ fontSize: "0.7rem", color: isExpanded ? "#2563eb" : "#94a3b8", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", lineHeight: 1 }}>▾</span>
      </div>

      {isExpanded && (
        <div style={{ borderTop: "1px solid #f1f5f9", padding: "0.875rem", background: "#fafbfc" }}>
          <div style={{ marginBottom: "0.875rem" }}>
            <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Task</div>
            <p style={{ margin: 0, fontSize: "0.8rem", color: "#0f172a", lineHeight: 1.6, fontWeight: 500 }}>{p.description}</p>
            {p.expectedColumns && (
  <div
    style={{
      marginTop: "10px",
      padding: "14px 16px",
      background: "#eff6ff",
      border: "1px solid #bfdbfe",
      borderRadius: "10px"
    }}
  >
    <div
      style={{
        fontSize: "12px",
        fontWeight: 700,
        color: "#2563eb",
        marginBottom: "10px",
        letterSpacing: "0.03em"
      }}
    >
      REQUIRED OUTPUT COLUMNS
    </div>

    {p.expectedColumns.map(col => (
      <div
        key={col}
        style={{
          fontSize: "13px",
          color: "#0f172a",
          marginBottom: "6px",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}
      >
        <span style={{ color: "#2563eb", fontSize: "14px" }}>•</span> {col}
      </div>
    ))}

    <div
      style={{
        marginTop: "10px",
        paddingTop: "10px",
        borderTop: "1px dashed #bfdbfe"
      }}
    />

    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        color: "#2563eb",
        fontWeight: 700,
        marginBottom: "8px"
      }}
    >
      <span style={{ fontSize: "14px" }}>📊</span> Expected Rows: {p.expectedRowCount}
    </div>

    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        fontSize: "13px",
        color: "#1d4ed8",
        lineHeight: 1.5
      }}
    >
      <span style={{ fontSize: "14px", flexShrink: 0 }}>ⓘ</span>
      <span>
        Return ONLY these columns. Additional columns will be marked incorrect.
      </span>
    </div>
  </div>
)}
          </div>
          <div style={{ marginBottom: "0.875rem" }}>
            <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>Explanation</div>
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#475569", lineHeight: 1.65 }}>{p.explanation}</p>
          </div>
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "0.625rem 0.75rem", marginBottom: "0.875rem" }}>
            <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "3px" }}>Real-world scenario</div>
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#1e40af", lineHeight: 1.6 }}>{p.basics}</p>
          </div>
          <div style={{ marginBottom: "0.875rem" }}>
            <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "5px" }}>Common use cases</div>
            {p.useCases.map((uc, i) => (
              <div key={i} style={{ display: "flex", gap: "6px", alignItems: "flex-start", marginBottom: "3px" }}>
                <span style={{ color: "#2563eb", fontSize: "0.7rem", marginTop: "2px" }}>→</span>
                <span style={{ fontSize: "0.77rem", color: "#475569", lineHeight: 1.5 }}>{uc}</span>
              </div>
            ))}
          </div>
          <details>
            <summary style={{ fontSize: "0.78rem", color: "#2563eb", fontWeight: 600, cursor: "pointer", listStyle: "none" }}>💡 Show hint</summary>
            <div style={{ marginTop: "6px", padding: "0.5rem 0.625rem", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", fontSize: "0.78rem", color: "#92400e", lineHeight: 1.6, fontFamily: "monospace" }}>
              {p.hint}
            </div>
          </details>
           {/* Table Structure — only show tables used in this problem */}
{/* {p.expectedColumns && (() => { */}
{(() => {
  // Detect which tables this problem uses based on solutionQuery or description
  const query = (p.solutionQuery || p.starterQuery || "").toLowerCase();
  const desc = (p.description || "").toLowerCase();
  const combined = query + " " + desc;

  const TABLE_SCHEMAS = {
    customers: ["customer_id","customer_name","email","phone","city","state","country","status","customer_type","lifetime_value", "created_at"],
    orders: ["order_id","customer_id","order_date","order_status","payment_status","total_amount","currency","delivered_date"],
    order_items: ["item_id","order_id","product_id","quantity","unit_price","total_price","item_status"],
    products: ["product_id","product_name","category","subcategory","brand","price","cost_price", "created_at"],
    payments: ["payment_id","order_id","payment_method","payment_status","amount","currency"],
    delivery_partners: ["delivery_partner_id","partner_name","vehicle_type","city","status","rating"],
    feedback: ["feedback_id","customer_id","order_id","rating","issue_category", "created_at"],
  };

  const usedTables = Object.keys(TABLE_SCHEMAS).filter(t => combined.includes(t));
  if (usedTables.length === 0) return null;

  return (
    <TableStructure tables={usedTables} />
  );
})()}
        </div>
      )}
    </div>
  );
}

function validateResults(userResult, referenceResult) {
  if (!userResult || !referenceResult) return null;

  // Row count check
  if (userResult.values.length !== referenceResult.values.length) {
    return "almost";
  }

  // Column count check
  if (
    userResult.columns.length >
    referenceResult.columns.length
  ) {
    return "extra_columns";
  }
  
  if (
    userResult.columns.length <
    referenceResult.columns.length
  ) {
    return "missing_columns";
  }

  // Normalize column names
  const normalizeColumn = (col) =>
    String(col).trim().toLowerCase();

  const userCols = userResult.columns.map(normalizeColumn).sort();
  const refCols = referenceResult.columns.map(normalizeColumn).sort();

  // Column names mismatch
  if (JSON.stringify(userCols) !== JSON.stringify(refCols)) {
    return "wrong_columns";
  }

  // Normalize values
  const normalizeValue = (v) => {
    if (v === null || v === undefined) return "null";

    // Handle numbers
    if (!isNaN(v) && v !== "") {
      return Number(v).toFixed(2);
    }

    return String(v).trim().toLowerCase();
  };

  // Normalize rows independent of column order
  const normalizeRows = (result) => {
    return result.values
      .map((row) =>
        row.map(normalizeValue).sort().join("|")
      )
      .sort()
      .join("\n");
  };

  const userNormalized = normalizeRows(userResult);
  const refNormalized = normalizeRows(referenceResult);

  if (userNormalized === refNormalized) {
    return "correct";
  }

  return "almost";
}

export default function SQLBasicsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { problemSlug } = useParams();
  const editorRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const selectedItemRef = useRef(null);

  const runCountRef = useRef(0);
  const [runCountDisplay, setRunCountDisplay] = useState(0);
  const [, setCommunityFeed] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedMilestone, setExpandedMilestone] = useState(null);
  const [selectedProblem, setSelectedProblem] = useState(SQL_SCENARIOS_PROBLEMS[0]);
  const [query, setQuery] = useState(SQL_SCENARIOS_PROBLEMS[0].starterQuery);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [db, setDb] = useState(null);
  const [dbReady, setDbReady] = useState(false);
  const [solvedIds, setSolvedIds] = useState(new Set());
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalComment, setModalComment] = useState("");
  const [postSuccess, setPostSuccess] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [elapsed, setElapsed] = useState(null); 
  const [userStreak, setUserStreak] = useState(0);
  const [unlockedBadges, setUnlockedBadges] = useState([]);
  const { isGuest, isPro, userEmail, userName: userFullName } = useProStatus();
  const isMobile = useMobile();

  const [code, setCode] = useState(SQL_SCENARIOS_PROBLEMS[0].starterCode || "");
  const [mobileCode, setMobileCode] = useState(SQL_SCENARIOS_PROBLEMS[0].starterCode || "");
  const [setOutput] = useState(null);

  const {
    formattedTimeLeft,
    percentLeft,
    timerColor,
    isExpired,
    isStopped,
    timeUsed,
    // totalTime,
    // startTimer,
    stopTimer,
    resetTimer,
    getPerformanceRating,
    formatTime,
    getExactTimeUsed, // ← ADD
  } = useTimer(selectedProblem?.difficulty, selectedProblem?.id);
  const [performanceRating, setPerformanceRating] = useState(null);
const [attemptNumber, setAttemptNumber] = useState(1);

  

  usePageMeta({
    title: selectedProblem
      ? `${selectedProblem.seoTitle} | Repractiq`
      : "SQL Scenarios Practice | Repractiq",
  
    description: selectedProblem
      ? selectedProblem.metaDescription
      : "Practice SQL Easy to Hard scenarios questions.",
  
    canonical: selectedProblem
      ? `https://www.repractiq.com/sql/scenarios/${selectedProblem.id}-${selectedProblem.slug}`
      : "https://www.repractiq.com/sql/scenarios"
  });

  const queryRef = useRef(query);
  useEffect(() => { queryRef.current = query; }, [query]);

  const selectedProblemRef = useRef(selectedProblem);
  useEffect(() => { selectedProblemRef.current = selectedProblem; }, [selectedProblem]);

  const dbRef = useRef(db);
  useEffect(() => { dbRef.current = db; }, [db]);

  useEffect(() => {
    const initDb = async () => {
      try {
        const initSqlJs = (await import("sql.js")).default;
        const SQL = await initSqlJs({
          locateFile: () => `${process.env.PUBLIC_URL}/sql-wasm.wasm`,
        });
        const database = new SQL.Database();

        const tables = [
          { name: "customers", columns: ["customer_id","customer_name","email","phone","city","state","country","postal_code","created_date","activated_date","last_login_date","last_order_date","status","customer_type","acquisition_channel","lifetime_value","is_verified", "created_at"] },
          { name: "orders", columns: ["order_id","customer_id","order_date","order_status","payment_status","delivery_partner_id","subtotal_amount","tax_amount","discount_amount","delivery_fee","total_amount","currency","estimated_delivery_time","delivered_date","cancelled_date","cancellation_reason"] },
          { name: "order_items", columns: ["item_id","order_id","product_id","quantity","unit_price","discount_amount","tax_amount","total_price","item_status","currency"] },
          { name: "products", columns: ["product_id","product_name","product_description","category","subcategory","brand","sku","price","cost_price","currency","is_active", "created_at"] },
          { name: "payments", columns: ["payment_id","order_id","payment_method","payment_provider","transaction_reference","payment_status","amount","currency","refund_amount","refund_date","failure_reason","payment_date","attempt_number"] },
          { name: "delivery_partners", columns: ["delivery_partner_id","partner_name","phone","vehicle_type","vehicle_number","city","status","joining_date","last_active_date","rating","total_deliveries"] },
          { name: "feedback", columns: ["feedback_id","customer_id","order_id","rating","review_text","feedback_channel","issue_category", "created_at"] },
        ];

        for (const table of tables) {
          const { data, error } = await supabase
            .from(table.name)
            .select(table.columns.join(","))
            .limit(1000);

          if (error || !data || data.length === 0) continue;

          const colDefs = table.columns.map(c => `"${c}" TEXT`).join(", ");
          database.run(`CREATE TABLE IF NOT EXISTS ${table.name} (${colDefs});`);

          const batchSize = 50;
          for (let i = 0; i < data.length; i += batchSize) {
            const batch = data.slice(i, i + batchSize);
            const placeholders = batch.map(() =>
              `(${table.columns.map(() => "?").join(",")})`
            ).join(",");
            const values = batch.flatMap(row =>
              table.columns.map(col => {
                const val = row[col];
                if (val === null || val === undefined) return null;
                return String(val);
              })
            );
            database.run(
              `INSERT INTO ${table.name} (${table.columns.map(c => `"${c}"`).join(",")}) VALUES ${placeholders}`,
              values
            );
          }
        }

        setDb(database);
        setDbReady(true);
      } catch (err) {
        console.error("SQL.js failed to load:", err);
      }
    };
    initDb();
  }, []);

  useEffect(() => {
    const fetchSolvedProblems = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) return;
      const userId = session.user.id;

  const { data: streakRow } = await supabase
  .from("user_streaks")
  .select("current_streak")
  .eq("user_id", userId)
  .maybeSingle();
setUserStreak(streakRow?.current_streak || 0);

      const { data, error } = await supabase
        .from("submissions")
        .select("problem_id")
        .eq("user_id", userId)
        .eq("category", "sql_basics")
        .eq("status", "correct");
      if (error || !data) return;
      const ids = new Set(data.map((row) => row.problem_id));
setSolvedIds(ids);

// Auto-open the milestone section where user currently is
const solvedCount = ids.size;
if (solvedCount < 25) setExpandedMilestone("bronze");
else if (solvedCount < 50) setExpandedMilestone("silver");
else setExpandedMilestone("gold");
      };
    fetchSolvedProblems();
  }, []);

  async function updateStreak(userId) {
    const today = new Date().toISOString().split("T")[0];
    const { data: existing } = await supabase
      .from("user_streaks")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();

    if (!existing) {
      await supabase.from("user_streaks").insert({
        user_id: userId,
        current_streak: 1,
        longest_streak: 1,
        last_solved_date: today,
      });
      return;
    }

    if (existing.last_solved_date === today) return;

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split("T")[0];

    const newStreak = existing.last_solved_date === yesterdayStr
      ? (existing.current_streak || 0) + 1
      : 1;

    await supabase.from("user_streaks").update({
      current_streak: newStreak,
      longest_streak: Math.max(newStreak, existing.longest_streak || 0),
      last_solved_date: today,
    }).eq("user_id", userId);
  }

  const runQuery = useCallback(async () => {
    const currentDb = dbRef.current;
    const currentQuery = queryRef.current;
    const currentProblem = selectedProblemRef.current;

    if (!currentDb) return;
    setError(null);
    setResults(null);

    try {
      const res = currentDb.exec(currentQuery);
      if (res.length === 0) {
        setError("Query executed but returned no rows. Check your logic.");
        return;
      }
    
      const resultData = res[0];
      setResults(resultData);
    
      runCountRef.current += 1;
      const newRunCount = runCountRef.current;
      setRunCountDisplay(newRunCount);
    
      // Run reference solution and validate
      let status = "attempted";
      if (currentProblem.solutionQuery) {
        try {
          const ref = currentDb.exec(currentProblem.solutionQuery);
          if (ref.length > 0) {
            status = validateResults(resultData, ref[0]);
            setValidationStatus(status);
            if (status === "correct") {
              const capturedTimeUsed = getExactTimeUsed(); // ← capture from ref first
  stopTimer();
  setPerformanceRating(getPerformanceRating(capturedTimeUsed));
              setSolvedIds(prev => new Set([...prev, currentProblem.id]));
              setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
              // Check for newly unlocked badges
              const { data: sessionData } = await supabase.auth.getSession();
              if (sessionData?.session?.user?.id) {
                const newBadges = await checkAndSaveBadges(sessionData.session.user.id);
                if (newBadges.length > 0) setUnlockedBadges(newBadges);
              }
            }
          }
        } catch (_) {
          // solution query failed silently — dont block user
        }
      }
    
      // Save to Supabase
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session) {
        const userId = sessionData.session.user.id;
    
        const { data: existing } = await supabase
          .from("submissions")
          .select("id, status")
          .eq("user_id", userId)
          .eq("problem_id", currentProblem.id)
          .eq("category", "sql_scenarios")
          .maybeSingle();
    
        // Never overwrite correct with worse
        if (existing?.status === "correct" && status !== "correct") {
          await updateStreak(userId);
          return;
        }
    
        if (existing) {
          await supabase.from("submissions").update({
            query: currentQuery,
            status,
            run_count: newRunCount,
            is_best_attempt: status === "correct",
            time_taken_seconds: getExactTimeUsed(),
          }).eq("id", existing.id);
        } else {
          await supabase.from("submissions").insert({
            user_id: userId,
            problem_id: currentProblem.id,
            category: "sql_scenarios",
            problem_title: currentProblem.title,
            query: currentQuery,
            status,
            run_count: newRunCount,
            is_best_attempt: status === "correct",
            time_taken_seconds: getExactTimeUsed(),
            attempt_number: attemptNumber,
          });
        }
    
        await updateStreak(userId);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [attemptNumber, getExactTimeUsed, getPerformanceRating, stopTimer]);

  const handleSelectProblem = useCallback((p) => {
    startTimeRef.current = Date.now();
    runCountRef.current = 0;
    setRunCountDisplay(0);
    setSelectedProblem(p);
    setQuery("-- Explore the data first, then write your solution below\nSELECT * FROM customers LIMIT 5;");
    setResults(null);
    setError(null);
    setValidationStatus(null);
    navigate(`/sql/scenarios/${p.id}-${p.slug}`);
  }, [navigate]);

  const handleToggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    const incoming = location.state || {};
    if (incoming.searchQuery) {
      setSearchInput(incoming.searchQuery);
      setSearchTerm(incoming.searchQuery);
    }
    if (incoming.focusProblemId !== undefined) {
      const targetProblem = SQL_SCENARIOS_PROBLEMS.find((p) => p.id === incoming.focusProblemId);
      if (targetProblem) {
        handleSelectProblem(targetProblem);
        setExpandedId(targetProblem.id);
      }
    } else {
      const idFromUrl = Number((problemSlug || "").split("-")[0]);
      if (!isNaN(idFromUrl)) {
        const target = SQL_SCENARIOS_PROBLEMS.find((p) => p.id === idFromUrl);
        if (target) {
          handleSelectProblem(target);
          setExpandedId(target.id);
        }
      }
    }
  }, [problemSlug, location.state, handleSelectProblem]);

  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedProblem]);

  const handlePostCommunity = () => {
    setShowModal(true);
    setModalComment("");
    setPostSuccess(false);
  };

  const submitPost = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session) {
      const userId = sessionData.session.user.id;
      await supabase.from("submissions").upsert({
        user_id: userId,
        problem_id: selectedProblem.id,
        category: "sql_scenarios",
        problem_title: selectedProblem.title,
        query: query,
        status: "attempted",
        is_best_attempt: true,
        comment: modalComment,
        run_count: runCountRef.current,
        time_taken_seconds: Math.floor((Date.now() - startTimeRef.current) / 1000),
      });
    }
    setCommunityFeed((prev) => [{
      user: "You",
      problem: selectedProblem.title,
      category: "sql_scenarios",
      query: query,
      comment: modalComment,
      time: "Just now",
    }, ...prev]);
    setPostSuccess(true);
    setTimeout(() => setShowModal(false), 1800);
  };

  const diffStyle = {
    Easy: {
      color: "#16a34a",   // Green 600
      bg: "#f0fdf4",      // Green 50
      border: "#bbf7d0",  // Green 200
    },
  
    Intermediate: {
      color: "#d97706",   // Amber 600
      bg: "#fffbeb",      // Amber 50
      border: "#fde68a",  // Amber 200
    },
  
    "Intermediate+": {
      color: "#ea580c",   // Orange 600
      bg: "#fff7ed",      // Orange 50
      border: "#fdba74",  // Orange 300
    },
  
    Advanced: {
      color: "#dc2626",   // Red 600
      bg: "#fef2f2",      // Red 50
      border: "#fecaca",  // Red 200
    },
  
    "Advanced+": {
      color: "#c026d3",   // Fuchsia 600
      bg: "#fdf4ff",      // Fuchsia 50
      border: "#f0abfc",  // Fuchsia 300
    },
  
    "Very Advanced": {
      color: "#7c3aed",   // Violet 600
      bg: "#f5f3ff",      // Violet 50
      border: "#c4b5fd",  // Violet 300
    },
  
    Elite: {
      color: "#4338ca",   // Indigo 700
      bg: "#eef2ff",      // Indigo 50
      border: "#a5b4fc",  // Indigo 300
    },
  
    "Elite+": {
      color: "#1f2937",   // Gray 800
      bg: "#f9fafb",      // Gray 50
      border: "#d1d5db",  // Gray 300
    },
  };
 
 
  const filteredProblems = useMemo(() => {
    if (!searchTerm.trim()) return SQL_SCENARIOS_PROBLEMS;
    return SQL_SCENARIOS_PROBLEMS.filter((p) => matchesProblem(p, searchTerm));
  }, [searchTerm]);

  const crossCategoryMatches = useMemo(
    () => searchSqlProblems(searchTerm).filter((m) => m.categoryKey !== "Scenarios").slice(0, 10),
    [searchTerm]
  );

  // ← ADD THIS BEFORE return
  const validationBanner = () => {
    if (!validationStatus) return null;
    const configs = {
      correct: {
        bg: "#f0fdf4", border: "#86efac", icon: "✓", iconColor: "#16a34a",
        title: "Correct!", msg: "Your output matches the expected result perfectly.",
        titleColor: "#15803d",
      },
      almost: {
        bg: "#fffbeb", border: "#fcd34d", icon: "~", iconColor: "#d97706",
        title: "Almost there", msg: "Your result structure looks right but the values don't match. Check your filters or logic.",
        titleColor: "#b45309",
      },
      wrong: {
        bg: "#fef2f2", border: "#fca5a5", icon: "✗", iconColor: "#dc2626",
        title: "Not quite", msg: "The returned rows do not match the expected output. Check your filters, joins, grouping, or calculations.",
        titleColor: "#b91c1c",
      },
      extra_columns: {
        bg: "#fef2f2",
        border: "#fca5a5",
        icon: "!",
        iconColor: "#dc2626",
        title: "Extra Columns Returned",
        msg: "Return only the columns requested in the problem statement.",
        titleColor: "#b91c1c",
      },
      missing_columns: {
        bg: "#fef2f2",
        border: "#fca5a5",
        icon: "!",
        iconColor: "#dc2626",
        title: "Missing Columns",
        msg: "One or more required columns are missing.",
        titleColor: "#b91c1c",
      },
      wrong_columns: {
        bg: "#fef2f2",
        border: "#fca5a5",
        icon: "!",
        iconColor: "#dc2626",
        title: "Incorrect Column Names",
        msg: "Column names do not match the required output.",
        titleColor: "#b91c1c",
      },
    };
    const c = configs[validationStatus];
    return (
      <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "10px", padding: "0.875rem 1rem", marginBottom: "1rem", display: "flex", gap: "10px", alignItems: "flex-start" }}>
        <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: c.iconColor, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}>
          {c.icon}
        </div>
        <div>
          <div style={{ fontSize: "0.85rem", fontWeight: 700, color: c.titleColor }}>{c.title}</div>
          <div style={{ fontSize: "0.8rem", color: "#475569", marginTop: "2px" }}>{c.msg}</div>
          {validationStatus === "correct" && (
  <div style={{ display: "flex", gap: "8px", marginTop: "0.5rem", flexWrap: "wrap" }}>
    <button
      onClick={handlePostCommunity}
      style={{ fontSize: "0.78rem", color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "6px", padding: "4px 12px", cursor: "pointer", fontWeight: 600 }}
    >
      🎉 Share to Community
    </button>
    <button
      onClick={() => setShareOpen(true)}
      style={{ fontSize: "0.78rem", color: "#0a66c2", background: "#e8f0fe", border: "1px solid #b0c4f7", borderRadius: "6px", padding: "4px 12px", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: "5px" }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="#0a66c2">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
      Share on LinkedIn
    </button>
  </div>
)}
        </div>
      </div>
    );
  };

  if (isMobile) {
    return (
      <MobileSQLLayout
        problems={SQL_SCENARIOS_PROBLEMS}
        selectedProblem={selectedProblem}
        onSelectProblem={handleSelectProblem}
        query={query}
        onQueryChange={setQuery}
        onRun={runQuery}
        onReset={() => { setQuery(selectedProblem.starterQuery); setResults(null); setError(null); }}
        onShowSolution={() => setQuery(selectedProblem.solutionQuery)}
        dbReady={dbReady}
        results={results}
        error={error}
        validationStatus={validationStatus}
        solvedIds={solvedIds}
        isGuest={isGuest}
        isPro={isPro}
        paywallThreshold={10}
        guestThreshold={10}
        onNavigateSignup={() => navigate("/signup")}
        onNavigateLogin={() => navigate("/login")}
        onNavigatePricing={() => navigate("/pricing")}
        pageTitle="SQL Scenarios"
        totalProblems={SQL_SCENARIOS_PROBLEMS.length}
        runCountDisplay={runCountDisplay}
        onPostCommunity={handlePostCommunity}
        setShareOpen={setShareOpen}
        user={{ fullName: userFullName, username: userFullName || userEmail?.split("@")[0] || "user" }}
        solvedCount={solvedIds.size}
        streak={userStreak}
        firstTry={runCountDisplay === 1}
        elapsed={elapsed}
        ShareModalComponent={ShareModal}
      />
    );
  }

  return (
    <div style={{ background: "#ffffff", height: "100vh", display: "flex", flexDirection: "column", fontFamily: "Inter, -apple-system, sans-serif", color: "#0f172a", overflow: "hidden" }}>
         <StructuredData
      problem={selectedProblem}
      category="scenarios"
      />
      {/* NAV */}
      <nav style={{ padding: "0.85rem 2rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.97)", flexShrink: 0 }}>
        <span onClick={() => navigate("/")} style={{ fontWeight: 800, cursor: "pointer", fontSize: "1.1rem", letterSpacing: "-0.3px" }}>Repractiq</span>
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <span onClick={() => navigate("/home")} style={{ cursor: "pointer", color: "#64748b", fontSize: "0.85rem", fontWeight: 500 }}>Home</span>
          <span onClick={() => navigate("/profile")} style={{ cursor: "pointer", color: "#64748b", fontSize: "0.85rem", fontWeight: 500 }}>Profile</span>
          <div style={{ fontSize: "0.78rem", color: "#16a34a", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "20px", padding: "4px 12px", fontWeight: 600 }}>
            ✓ {solvedIds.size} / {SQL_SCENARIOS_PROBLEMS.length} attempted
          </div>
          <span onClick={() => navigate("/sql")} style={{ cursor: "pointer", color: "#2563eb", fontSize: "0.85rem", fontWeight: 600 }}>← Back to Practice</span>
        </div>
      </nav>

      {/* PAGE TITLE */}
      <div style={{ background: "linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)", borderBottom: "1px solid #e2e8f0", padding: "0.875rem 2rem", display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
        <h2 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, letterSpacing: "-0.3px", color: "#0f172a" }}>SQL Scenarios</h2>
      </div>

      {/* MAIN SPLIT */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* LEFT PANEL */}
        <div style={{ width: "340px", minWidth: "300px", borderRight: "1px solid #e2e8f0", overflowY: "auto", background: "#f8fafc", flexShrink: 0 }}>
          <div style={{ padding: "1rem 1rem 0.5rem" }}>
            <span style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Questions</span>
            <div style={{ marginTop: "0.65rem", display: "flex", gap: "8px" }}>
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setSearchTerm(searchInput.trim()); }}
                placeholder="Search SQL topics (joins, window...)"
                style={{ flex: 1, fontSize: "0.75rem", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "7px 9px", outline: "none" }}
              />
              <button
                onClick={() => setSearchTerm(searchInput.trim())}
                style={{ fontSize: "0.75rem", border: "1px solid #bfdbfe", color: "#2563eb", background: "#eff6ff", borderRadius: "8px", padding: "7px 10px", cursor: "pointer", fontWeight: 600 }}
              >
                Search
              </button>
            </div>
          </div>

          {!!searchTerm && (
            <div style={{ margin: "0 0.75rem 0.5rem", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "0.625rem 0.75rem" }}>
              <div style={{ fontSize: "0.72rem", color: "#1d4ed8", fontWeight: 700, marginBottom: "4px" }}>
                Results: {filteredProblems.length} in Scenarios
              </div>
              {crossCategoryMatches.length > 0 && (
                <div style={{ fontSize: "0.72rem", color: "#475569" }}>
                  Also found in other categories:
                  {crossCategoryMatches.map((m, i) => (
                    <button
                      key={`${m.categoryKey}-${m.problem.id}-${i}`}
                      onClick={() => navigate(m.route, { state: { searchQuery: searchTerm, focusProblemId: m.problem.id } })}
                      style={{ marginLeft: "6px", marginTop: "6px", border: "1px solid #cbd5e1", background: "#fff", color: "#334155", borderRadius: "999px", padding: "3px 8px", fontSize: "0.68rem", cursor: "pointer" }}
                    >
                      {m.problem.title} ({m.categoryLabel})
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* When searching, show flat list */}
{searchTerm ? (
  <>
    {filteredProblems.map((p) => (
      <ProblemRow
        key={p.id}
        p={p}
        isSelected={selectedProblem.id === p.id}
        isExpanded={expandedId === p.id}
        isSolved={solvedIds.has(p.id)}
        isLocked={isGuest && p.id > 10}
        selectedItemRef={selectedItemRef}
        diffStyle={diffStyle}
        onSelect={() => {
          handleSelectProblem(p);
          handleToggleExpand(p.id);
          if (editorRef.current) editorRef.current.focus();
        }}
      />
    ))}
  </>
) : (
  <>
    {MILESTONES.map((milestone) => {
      const milestoneProblems = SQL_SCENARIOS_PROBLEMS.filter(
        (p) => p.id >= milestone.range[0] && p.id <= milestone.range[1]
      );
      const solvedInMilestone = milestoneProblems.filter((p) => solvedIds.has(p.id)).length;
      const totalInMilestone = milestoneProblems.length;
      const progressPct = Math.round((solvedInMilestone / totalInMilestone) * 100);
      const isEarned = solvedInMilestone >= totalInMilestone;
      const isOpen = expandedMilestone === milestone.id;

      return (
        <div key={milestone.id} style={{ margin: "0 0.75rem 0.75rem" }}>
          {/* Milestone Header */}
          <div
            onClick={() => setExpandedMilestone(isOpen ? null : milestone.id)}
            style={{
              background: isEarned ? milestone.bg : "#f8fafc",
              border: `1.5px solid ${isEarned ? milestone.border : "#e2e8f0"}`,
              borderRadius: isOpen ? "10px 10px 0 0" : "10px",
              padding: "0.75rem 0.875rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <span style={{ fontSize: "1.25rem", lineHeight: 1 }}>{milestone.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: isEarned ? milestone.color : "#0f172a" }}>
                {milestone.label}
              </div>
              <div style={{ fontSize: "0.65rem", color: "#94a3b8", marginTop: "2px" }}>
                Problems {milestone.range[0]}–{milestone.range[1]}
              </div>
              {/* Progress bar */}
              <div style={{ marginTop: "6px", height: "3px", background: "#e2e8f0", borderRadius: "2px", overflow: "hidden" }}>
                <div style={{ width: `${progressPct}%`, height: "100%", background: isEarned ? milestone.color : "#2563eb", borderRadius: "2px", transition: "width 0.4s ease" }} />
              </div>
              <div style={{ fontSize: "0.62rem", color: "#94a3b8", marginTop: "3px" }}>
                {solvedInMilestone}/{totalInMilestone} solved
              </div>
            </div>
            <span style={{ fontSize: "0.7rem", color: isOpen ? "#2563eb" : "#94a3b8", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▾</span>
          </div>

          {/* Problems inside milestone */}
          {isOpen && (
            <div style={{ border: "1.5px solid #e2e8f0", borderTop: "none", borderRadius: "0 0 10px 10px", overflow: "hidden" }}>
              {milestoneProblems.map((p) => (
                <ProblemRow
                  key={p.id}
                  p={p}
                  isSelected={selectedProblem.id === p.id}
                  isExpanded={expandedId === p.id}
                  isSolved={solvedIds.has(p.id)}
                  isLocked={isGuest && p.id > 10}
                  selectedItemRef={selectedItemRef}
                  diffStyle={diffStyle}
                  onSelect={() => {
                    handleSelectProblem(p);
                    handleToggleExpand(p.id);
                    if (editorRef.current) editorRef.current.focus();
                  }}
                  nested
                />
              ))}
            </div>
          )}
        </div>
      );
    })}
  </>
)}
<div style={{ height: "1.5rem" }} />
        </div>

        {/* RIGHT PANEL */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#ffffff" }}>
          {selectedProblem.id > 10 && !isGuest && !isPro ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
              <div style={{ textAlign: "center", maxWidth: "380px" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🔒</div>
                <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", margin: "0 0 0.5rem" }}>
                  Pro problem
                </h3>
                <p style={{ fontSize: "0.88rem", color: "#64748b", lineHeight: 1.7, marginBottom: "1.5rem" }}>
                  You've completed the free tier. Upgrade to Pro to unlock all {SQL_SCENARIOS_PROBLEMS.length} SQL Scenarios problems.
                </p>
                <button
                  onClick={() => navigate("/pricing")}
                  style={{ width: "100%", padding: "11px", borderRadius: "8px", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: "0.88rem", border: "none", cursor: "pointer", marginBottom: "8px" }}
                >
                  View Pro Plans →
                </button>
                <button
                  onClick={() => navigate("/pricing")}
                  style={{ width: "100%", padding: "11px", borderRadius: "8px", background: "#ffffff", color: "#64748b", fontWeight: 600, fontSize: "0.88rem", border: "1.5px solid #e2e8f0", cursor: "pointer" }}
                >
                  See what's included
                </button>
              </div>
            </div>
          ) : selectedProblem.id > 10 && isGuest ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
              <div style={{ textAlign: "center", maxWidth: "360px" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🔒</div>
                <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", margin: "0 0 0.5rem" }}>Sign in to unlock this problem</h3>
                <p style={{ fontSize: "0.88rem", color: "#64748b", lineHeight: 1.7, marginBottom: "1.5rem" }}>
                  You've explored the first 10 problems. Sign up free to access all {SQL_SCENARIOS_PROBLEMS.length} SQL problems and save your progress.
                </p>
                <button onClick={() => navigate("/signup")} style={{ width: "100%", padding: "11px", borderRadius: "8px", background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: "0.88rem", border: "none", cursor: "pointer", marginBottom: "8px" }}>
                  Sign Up Free →
                </button>
                <button onClick={() => navigate("/login")} style={{ width: "100%", padding: "11px", borderRadius: "8px", background: "#ffffff", color: "#2563eb", fontWeight: 600, fontSize: "0.88rem", border: "1.5px solid #bfdbfe", cursor: "pointer" }}>
                  Already have an account? Sign in
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Problem Header */}
              <div style={{ padding: "1.25rem 1.75rem 1rem", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "6px" }}>
                      <span style={{ fontSize: "0.7rem", padding: "3px 10px", borderRadius: "10px", background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", fontWeight: 600 }}>Easy</span>
                      <span style={{ fontSize: "0.7rem", color: "#94a3b8" }}>#{selectedProblem.id}</span>
                      {solvedIds.has(selectedProblem.id) && (
                        <span style={{ fontSize: "0.7rem", padding: "3px 10px", borderRadius: "10px", background: "#f0fdf4", color: "#16a34a", fontWeight: 600 }}>✓ Attempted</span>
                      )}
                    </div>
                    <h1 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 800, letterSpacing: "-0.3px", color: "#0f172a" }}>{selectedProblem.title}</h1>
                  </div>
                  {/* ← ADD TIMER HERE */}
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <TimerDisplay
        formattedTimeLeft={formattedTimeLeft}
        percentLeft={percentLeft}
        timerColor={timerColor}
        isExpired={isExpired}
        isStopped={isStopped}
        difficulty={selectedProblem?.difficulty || "Easy"}
        timeUsed={timeUsed}
        formatTime={formatTime}
      />
      </div>
                  {/* <button
                    onClick={handlePostCommunity}
                    style={{ padding: "8px 16px", borderRadius: "8px", background: "#ffffff", color: "#2563eb", fontWeight: 600, fontSize: "0.8rem", border: "1.5px solid #bfdbfe", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}
                  >
                    🌐 Post to Community
                  </button> */}
                </div>
                <div style={{ marginTop: "0.875rem", background: "#f8fafc", border: "1px solid #e2e8f0", borderLeft: "3px solid #2563eb", borderRadius: "0 8px 8px 0", padding: "0.625rem 0.875rem" }}>
                  <span style={{ fontSize: "0.67rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: "3px" }}>Task</span>
                  <p style={{ margin: 0, fontSize: "0.88rem", color: "#0f172a", lineHeight: 1.6 }}>{selectedProblem.description}</p>
                </div>
              </div>

              {/* Editor + Results */}
              <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem 1.75rem" }}>
                    {/* ← ADD PERFORMANCE RATING */}
  {performanceRating && (
    <PerformanceRating
      rating={performanceRating}
      onShare={handlePostCommunity}
      onNext={() => {
        const currentIndex = SQL_SCENARIOS_PROBLEMS.findIndex(p => p.id === selectedProblem.id);
        const next = SQL_SCENARIOS_PROBLEMS[currentIndex + 1];
        if (next) handleSelectProblem(next);
      }}
    />
  )}
              {validationBanner()}

                {/* Editor */}
                <div style={{ border: "1.5px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", marginBottom: "1rem" }}>
                  <div style={{ background: "#f8fafc", padding: "0.625rem 1rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <span style={{ fontSize: "0.7rem", background: "#e2e8f0", color: "#0f172a", padding: "3px 9px", borderRadius: "20px", fontWeight: 700 }}>SQL</span>
                      <span style={{ fontSize: "0.72rem", color: "#94a3b8" }}>Ctrl+Enter to run</span>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                    <button
  onClick={() => { setQuery("-- Explore the data first, then write your solution below\nSELECT * FROM customers LIMIT 5;"); setResults(null); setError(null); setValidationStatus(null); }}
  style={{ fontSize: "0.75rem", color: "#64748b", background: "transparent", border: "1px solid #e2e8f0", borderRadius: "6px", padding: "4px 10px", cursor: "pointer" }}
>
  Reset
</button>
<button
  onClick={() => { setQuery(selectedProblem.solutionQuery); setResults(null); setValidationStatus(null); }}
  style={{ fontSize: "0.75rem", color: "#d97706", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", padding: "4px 10px", cursor: "pointer", fontWeight: 600 }}
>
  💡 Solution
</button>
                      <button
                        onClick={runQuery}
                        disabled={!dbReady}
                        style={{ padding: "6px 18px", borderRadius: "6px", background: dbReady ? "#2563eb" : "#94a3b8", color: "#fff", fontWeight: 700, fontSize: "0.8rem", border: "none", cursor: dbReady ? "pointer" : "not-allowed" }}
                      >
                        {dbReady ? "▶ Run" : "Loading…"}
                      </button>
                    </div>
                  </div>
                  <Editor
                    height="400px"
                    language="sql"
                    value={query}
                    onChange={(value) => setQuery(value || "")}
                    theme="vs-dark"
                    options={{ fontSize: 14, minimap: { enabled: false }, wordWrap: "on", scrollBeyondLastLine: false, padding: { top: 10, bottom: 10 }, lineNumbers: "on" }}
                    onMount={(editor) => {
                      editorRef.current = editor;
                      editor.addCommand(window.monaco.KeyMod.CtrlCmd | window.monaco.KeyCode.Enter, () => runQuery());
                    }}
                  />
                </div>

                {/* Results */}
                <div style={{ border: "1.5px solid #e2e8f0", borderRadius: "12px", overflow: "hidden" }}>
                  <div style={{ background: "#f8fafc", padding: "0.625rem 1rem", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Output</span>
                    {results && (
                      <span style={{ fontSize: "0.72rem", color: "#94a3b8" }}>{results.values.length} row{results.values.length !== 1 ? "s" : ""}</span>
                    )}
                  </div>
                  <div style={{ minHeight: "120px", padding: "0.875rem 1rem", background: "#ffffff" }}>
                    {!results && !error && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100px", color: "#94a3b8", fontSize: "0.82rem" }}>
                        Run your query to see results here
                      </div>
                    )}
                    {error && (
                      <div style={{ color: "#ef4444", fontSize: "0.82rem", fontFamily: "monospace", lineHeight: 1.6, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "8px", padding: "0.75rem" }}>
                        {error}
                      </div>
                    )}
                    {results && (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ borderCollapse: "collapse", fontSize: "0.8rem", width: "100%" }}>
                          <thead>
                            <tr>
                              {results.columns.map((col) => (
                                <th key={col} style={{ textAlign: "left", padding: "6px 12px", color: "#64748b", fontWeight: 600, borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {results.values.map((row, i) => (
                              <tr key={i} style={{ background: i % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                                {row.map((cell, j) => (
                                  <td key={j} style={{ padding: "7px 12px", color: "#0f172a", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                {runCountDisplay > 2 && validationStatus !== "correct" && (
                  <div style={{ marginTop: "1rem", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "0.75rem 1rem", fontSize: "0.8rem", color: "#92400e" }}>
                    <strong>Stuck?</strong> Click the question on the left and expand the hint section.
                  </div>
                )}

                <div style={{ height: "2rem" }} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* TIMES UP MODAL */}
{isExpired && (
  <TimesUpModal
    problem={selectedProblem}
    runCount={runCountDisplay}
    difficulty={selectedProblem?.difficulty || "Easy"}
    onReset={() => {
      // New attempt — reset everything
      setAttemptNumber(prev => prev + 1);
      setCode(selectedProblem.starterCode || "");
      setMobileCode(selectedProblem.starterCode || "");
      setOutput(null);
      setError(null);
      setValidationStatus(null);
      setPerformanceRating(null);
      runCountRef.current = 0;
      setRunCountDisplay(0);
      resetTimer(selectedProblem?.difficulty || "Easy");
    }}
    onSkip={() => {
      // Save skipped to Supabase then move to next
      supabase.auth.getSession().then(({ data: sessionData }) => {
        if (sessionData?.session) {
          supabase.from("submissions").insert({
            user_id: sessionData.session.user.id,
            problem_id: selectedProblem.id,
            category: "sql_scenarios",
            problem_title: selectedProblem.title,
            query: isMobile ? mobileCode : code,
            status: "skipped",
            run_count: runCountDisplay,
            is_best_attempt: false,
            time_taken_seconds: timeUsed,
            attempt_number: attemptNumber,
          });
        }
      });
      const currentIndex = SQL_SCENARIOS_PROBLEMS.findIndex(p => p.id === selectedProblem.id);
      const next = SQL_SCENARIOS_PROBLEMS[currentIndex + 1];
      if (next) handleSelectProblem(next);
    }}
    onClose={() => resetTimer(selectedProblem?.difficulty || "Easy")}
  />
)}

      {/* COMMUNITY POST MODAL */}
      {showModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div style={{ background: "#ffffff", borderRadius: "16px", padding: "1.75rem", width: "480px", maxWidth: "90vw", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
            {postSuccess ? (
              <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>🎉</div>
                <div style={{ fontWeight: 800, fontSize: "1.1rem", color: "#0f172a" }}>Posted to Community!</div>
                <div style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "4px" }}>Your solution is now live on the community feed.</div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "1.25rem" }}>
                  <h3 style={{ margin: "0 0 4px", fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Share to Community</h3>
                  <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b" }}>Your query and comment will appear in the community feed.</p>
                </div>
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "0.625rem 0.875rem", marginBottom: "1rem", fontSize: "0.82rem", color: "#0f172a", fontWeight: 600 }}>
                  📝 {selectedProblem.title}
                </div>
                <div style={{ background: "#0f172a", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem", fontFamily: "monospace", fontSize: "0.8rem", color: "#7dd3fc", whiteSpace: "pre-wrap", maxHeight: "100px", overflowY: "auto" }}>
                  {query}
                </div>
                <div style={{ marginBottom: "1.25rem" }}>
                  <label style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "6px" }}>
                    Add a comment (optional)
                  </label>
                  <textarea
                    value={modalComment}
                    onChange={(e) => setModalComment(e.target.value)}
                    placeholder="Share what you learned, a tip, or a question..."
                    rows={3}
                    style={{ width: "100%", padding: "0.625rem 0.75rem", border: "1.5px solid #e2e8f0", borderRadius: "8px", fontSize: "0.85rem", fontFamily: "Inter, sans-serif", outline: "none", resize: "none", color: "#0f172a", boxSizing: "border-box" }}
                    onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
                    onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                  />
                </div>
                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                  <button onClick={() => setShowModal(false)} style={{ padding: "8px 18px", borderRadius: "8px", border: "1.5px solid #e2e8f0", background: "#ffffff", color: "#64748b", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                    Cancel
                  </button>
                  <button onClick={submitPost} style={{ padding: "8px 22px", borderRadius: "8px", border: "none", background: "#2563eb", color: "#ffffff", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
                    Post →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <ShareModal
  isOpen={shareOpen}
  onClose={() => setShareOpen(false)}
  problem={{ ...selectedProblem, category: "Scenarios" }}
  user={{
    fullName: userFullName,
    username: userFullName || userEmail?.split("@")[0] || "user",
  }}
  solvedCount={solvedIds.size}
  streak={userStreak}
  firstTry={runCountDisplay === 1}
  timeTaken={elapsed}
/>
<BadgeUnlockModal
  badges={unlockedBadges}
  isOpen={unlockedBadges.length > 0}
  onClose={() => setUnlockedBadges([])}
  onViewBadges={() => navigate("/profile?tab=badges")}
  isMobile={isMobile}
/>
    </div>
  );
}